
/*

Summary:
The function `calculateTotals` computes aggregate totals for database scenarios including RDS, Aurora, ODB@AWS, and RDS Consolidation metrics.

Description:
1. Initializes a totals array with one object per scenario containing all metric fields set to default values
2. Iterates through batchItems to accumulate RDS and Aurora totals, respecting PDB/CDB and duplicate database exclusion rules
3. If infrastructure data is provided, accumulates ODB@AWS totals
4. If consolidatedServers data is provided, accumulates RDS Consolidation totals
5. Returns the totals array along with sorted scenario names

Parameters:
1. data (object):
   - scenarioNames (Set): Set of unique scenario names
   - batchItems (array): Array of database items to process
   - infrastructure (array, optional): Array of ODB@AWS infrastructure configurations
   - consolidatedServers (array, optional): Array of consolidated RDS servers
   
Returns an object with:
   - totals (array): Array of objects, one per scenario, containing all calculated totals
   - totalScenarioNames (array): Sorted array of scenario names
   
*/

function calculateTotals(data) {
  const { scenarioNames, batchItems, infrastructure, consolidatedServers, showAlertNonX86 } = data;
  
  const totalScenarioNames = [...scenarioNames].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  const numScenarios = totalScenarioNames.length;
  
  const totals = [];
  for (let i = 0; i < numScenarios; i++) {
    totals.push({
      scenarioName: totalScenarioNames[i],
      totalRDScost: 0,
      totalRDSComputecost: 0,
      totalRDSStoragecost: 0,
      totalUnshapedRDS: 0,
      totalRDSVCPU: 0,
      totalRDSCount: 0,
      totalRDSInstanceCount: 0,
      totalAuroracost: 0,
      totalAuroraComputecost: 0,
      totalAuroraStoragecost: 0,
      totalUnshapedAurora: 0,
      totalAuroraVCPU: 0,
      totalAuroraCount: 0,
      totalAuroraInstanceCount: 0,
      totalEC2FSxNcost: 0,
      totalEC2FSxNComputecost: 0,
      totalEC2FSxNStoragecost: 0,
      totalUnshapedEC2FSxN: 0,
      totalEC2FSxNVCPU: 0,
      totalEC2FSxNCores: 0,
      totalEC2FSxNCount: 0,
      totalEC2FSxNInstanceCount: 0,
      totalADBSCount: 0,
      totalUnshapedADBS: 0,
      totalADBSCost: 0,
      totalADBSEcpus: 0,
      totalADBSEcpuCost: 0,
      totalADBSStorageCost: 0,
      totalODBUsage: 0,
      totalODBUsageUnits: "",
      totalODBcost: 0,
      totalODBdbservercost: 0,
      totalODBstorageservercost: 0,
      totalODBUsagecost: 0,
      totalRDSConsolidatedcost: 0,
      totalRDSConsolidatedComputecost: 0,
      totalRDSConsolidatedStoragecost: 0,
      totalRDSConsolidatedVCPU: 0,
      totalRDSConsolidatedCount: 0,
      totalSourceDBCount: 0,
      totalSourceInstanceCount: 0,
      totalSourceVCPU: 0,
      totalSourceCores: 0,
      totalSourceUsableDiskSpace: 0,
      totalSourceDistinctServerCount: 0,
      totalSourceDistinctServerCores: 0,
      totalSourceDistinctServerVCPUs: 0,
      pdb_and_cdb_algo: "",
      duplicate_database_exclude_algo: "",
      readreplica_algo: "",
      rds_target_option_tag: "",
      odb_target_option_tag: "",
      sourceExadata: false
    });
  }
  
  let sourceReplica = false;
  let rdsReplica = false;
  let sourceOverlaps = false;
  
  // Calculate totals from batchItems
  
  // Track distinct hostnames per scenario
  const scenarioHostnames = {}; // Map of scenarioName -> Map of hostname -> {cores, vcpus}
  
  for (let groupi = 0; groupi < batchItems.length; groupi++) {
    const item = batchItems[groupi];
    const totalScenarioIndex = totalScenarioNames.indexOf(item.scenarioName);
    
    if (item.frozen_prefs) {
      totals[totalScenarioIndex].pdb_and_cdb_algo = item.frozen_prefs.pdb_and_cdb_exclude_algo;
      totals[totalScenarioIndex].duplicate_database_exclude_algo = item.frozen_prefs.duplicate_database_exclude_algo;
      totals[totalScenarioIndex].readreplica_algo = item.frozen_prefs.readreplica_algo;
      totals[totalScenarioIndex].rds_target_option_tag = item.frozen_prefs.rds_algo=="ee" ? "rds_oracle_enterprise_edition" : "rds_oracle_se2_li";
      
      if (item.frozen_prefs.odbAWS_algo === "Y" && item.frozen_prefs.odbAWS_service && item.frozen_prefs.odbAWS_Licensing) {
        const serviceMap = { 'exacs': 'odb_exacsd', 'exascale': 'odb_exacsd', 'adb': 'odb_adbd', 'adbs-tp': 'odb_adbs' };
        const svc = serviceMap[item.frozen_prefs.odbAWS_service] || 'odb_exacsd';
        totals[totalScenarioIndex].odb_target_option_tag = svc + (item.frozen_prefs.odbAWS_Licensing === 'LI' ? '_li' : '_byol');
      }
    }
    
    // Check if we include database in the totals
    const includePDBCDB = (item.PDBandCDBmatch === undefined) ||
      (!item.frozen_prefs || (item.frozen_prefs.pdb_and_cdb_exclude_algo === "none") ||
        ((item.frozen_prefs.pdb_and_cdb_exclude_algo === "excludePDB") && (item.PDBandCDBmatch !== "PDBwithParent")) ||
        ((item.frozen_prefs.pdb_and_cdb_exclude_algo === "excludeCDB") && (item.PDBandCDBmatch !== "CDBwithChild")));
    
    const includeDuplicate = (item.DuplicateMatch === undefined) ||
      (!item.frozen_prefs || (item.frozen_prefs.duplicate_database_exclude_algo === "none") ||
        ((item.frozen_prefs.duplicate_database_exclude_algo === "excludeDuplicates") && (item.DuplicateMatch === "OriginalDatabase")));

    
    // for Source environment, use simple include logic
    if ( ((item.PDBandCDBmatch === undefined) || (item.PDBandCDBmatch !== "PDBwithParent")) &&
        ((item.DuplicateMatch === undefined) || (item.DuplicateMatch === "OriginalDatabase")) ) {
      // create totals for source environment
      totals[totalScenarioIndex].totalSourceDBCount += 1;
      totals[totalScenarioIndex].totalSourceInstanceCount += 1*item.NUM_INSTANCES;
      totals[totalScenarioIndex].totalSourceVCPU += 1*item.NUM_INSTANCES* item.NUM_CPUS;
      totals[totalScenarioIndex].totalSourceCores += 1*item.NUM_INSTANCES* item.NUM_CPU_CORES;
      totals[totalScenarioIndex].totalSourceUsableDiskSpace += 1*item.DiskSpace;
      if (item.Exadata === 'Yes') totals[totalScenarioIndex].sourceExadata = true;

      if ((item.DATABASE_ROLE != undefined && item.DATABASE_ROLE!='PRIMARY')) {
        sourceReplica=true;
      }

      // Track distinct hostnames for this scenario
      if (item.hostname) {
        if (!scenarioHostnames[item.scenarioName]) {
          scenarioHostnames[item.scenarioName] = new Map();
        }
        
        const hostnames = item.hostname.split(',').map(h => h.trim()).filter(h => h);
        for (const hostname of hostnames) {
          const existing = scenarioHostnames[item.scenarioName].get(hostname);
          if (existing) {
            // Check for conflicts
            if (existing.cores !== item.NUM_CPU_CORES*1 || existing.vcpus !== item.NUM_CPUS*1) {
              console.log(`Hostname ${hostname} has conflicting CPU values. Existing: ${existing.cores} cores, ${existing.vcpus} vcpus. New: ${item.NUM_CPU_CORES} cores, ${item.NUM_CPUS} vcpus. Using higher values.`);
              existing.cores = Math.max(existing.cores, item.NUM_CPU_CORES*1);
              existing.vcpus = Math.max(existing.vcpus, item.NUM_CPUS*1);
            }
          } else {
            scenarioHostnames[item.scenarioName].set(hostname, {
              cores: item.NUM_CPU_CORES*1,
              vcpus: item.NUM_CPUS*1,
              current_hw_memory: item.current_hw_memory,
              PLATFORM_NAME: item.PLATFORM_NAME
            });
          }
        }
      }
      
    } else {
      sourceOverlaps = true;
    }




    // For RDS scenarios, use more complex include logic
    if (includePDBCDB && includeDuplicate) {
      

      if (item.ReadReplica === 'Y') {
        rdsReplica=true;
      }
      
      
      if (item.rds_shape === 'N/A') {
        totals[totalScenarioIndex].totalUnshapedRDS++;
      } else {
        totals[totalScenarioIndex].totalRDScost += item.targetconfig_rds.monthly_estimate * 1;
        totals[totalScenarioIndex].totalRDSComputecost += item.targetconfig_rds.shape_cost * 1;
        totals[totalScenarioIndex].totalRDSStoragecost += item.targetconfig_rds.ebs_data_cost * 1;
        totals[totalScenarioIndex].totalRDSCount += 1;
        totals[totalScenarioIndex].totalRDSInstanceCount += 1;
        totals[totalScenarioIndex].totalRDSVCPU += item.targetconfig_rds.shape_max_vcpu * 1;
        if (item.UseMAZ === "Yes") {
          totals[totalScenarioIndex].totalRDSVCPU += item.targetconfig_rds.shape_max_vcpu * 1;
          totals[totalScenarioIndex].totalRDSInstanceCount += 1;
        }
      }
      
      if (item.targetconfig_aurora !== undefined) {
        if (item.aurora_shape === 'N/A') {
          totals[totalScenarioIndex].totalUnshapedAurora++;
        } else {
          totals[totalScenarioIndex].totalAuroraCount += 1;
          totals[totalScenarioIndex].totalAuroraInstanceCount += 1;
          totals[totalScenarioIndex].totalAuroracost += item.targetconfig_aurora.monthly_estimate * 1;
          totals[totalScenarioIndex].totalAuroraComputecost += item.targetconfig_aurora.shape_cost * 1;
          totals[totalScenarioIndex].totalAuroraStoragecost += item.targetconfig_aurora.ebs_data_cost * 1;
          totals[totalScenarioIndex].totalAuroraVCPU += item.targetconfig_aurora.shape_max_vcpu * 1;
          if (item.UseMAZ === "Yes") {
            totals[totalScenarioIndex].totalAuroraVCPU += item.targetconfig_aurora.shape_max_vcpu * 1;
            totals[totalScenarioIndex].totalAuroraInstanceCount += 1;
          }
        }
      }

      if (item.targetconfig_ec2fsxn !== undefined) {
        const ec2fsxn = item.targetconfig_ec2fsxn;
        // monthly_estimate is a positive number when sized; the unsizable cases
        // set it to the string "N/A" or "null", so a non-positive/NaN value means
        // this database was not sized for EC2+FSxN.
        const ec2fsxnMonthly = ec2fsxn.monthly_estimate * 1;
        if (isNaN(ec2fsxnMonthly) || ec2fsxnMonthly <= 0) {
          totals[totalScenarioIndex].totalUnshapedEC2FSxN++;
        } else {
          // shape_quantity ("1" or "2") and shape_cost/total_cores_needed already
          // encode the Multi-AZ HA factor, so no extra UseMAZ doubling is needed.
          const ec2fsxnQty = (ec2fsxn.shape_quantity * 1) || 1;
          totals[totalScenarioIndex].totalEC2FSxNCount += 1;
          totals[totalScenarioIndex].totalEC2FSxNInstanceCount += ec2fsxnQty;
          totals[totalScenarioIndex].totalEC2FSxNcost += ec2fsxnMonthly;
          totals[totalScenarioIndex].totalEC2FSxNComputecost += ec2fsxn.shape_cost * 1;
          totals[totalScenarioIndex].totalEC2FSxNStoragecost += ec2fsxn.fsxn_data_cost * 1;
          totals[totalScenarioIndex].totalEC2FSxNVCPU += ec2fsxn.shape_max_vcpu * ec2fsxnQty;
          totals[totalScenarioIndex].totalEC2FSxNCores += ec2fsxn.total_cores_needed * 1;
        }
      }

      if (item.targetconfig_adbs !== undefined) {
        if (item.targetconfig_adbs.monthly_estimate === null) {
          totals[totalScenarioIndex].totalUnshapedADBS += 1;
        } else {
          totals[totalScenarioIndex].totalADBSCount += 1;
          totals[totalScenarioIndex].totalADBSCost += item.targetconfig_adbs.monthly_estimate * 1;
          totals[totalScenarioIndex].totalADBSEcpus += item.targetconfig_adbs.ecpu_billed * 1;
          totals[totalScenarioIndex].totalADBSEcpuCost += item.targetconfig_adbs.ecpu_cost * 1;
          totals[totalScenarioIndex].totalADBSStorageCost += item.targetconfig_adbs.storage_cost * 1;
        }
      }
    }
  }
  // Calculate distinct server totals per scenario
  for (let i = 0; i < numScenarios; i++) {
    const scenarioName = totalScenarioNames[i];
    if (scenarioHostnames[scenarioName]) {
      const hostnameMap = scenarioHostnames[scenarioName];
      totals[i].totalSourceDistinctServerCount = hostnameMap.size;
      
      let totalCores = 0;
      let totalVCPUs = 0;
      let hostnames = [];
      for (const [hostname, cpuInfo] of hostnameMap) {
        totalCores += cpuInfo.cores;
        totalVCPUs += cpuInfo.vcpus;
        hostnames.push({hostname: hostname, cores: cpuInfo.cores, vcpus: cpuInfo.vcpus, memory: cpuInfo.current_hw_memory, platform: cpuInfo.PLATFORM_NAME});
      }
      totals[i].totalSourceDistinctServerCores = totalCores;
      totals[i].totalSourceDistinctServerVCPUs = totalVCPUs;
      totals[i].sourceDistinctServers = hostnames;
      
      totals[i].sourceWarning="";
      totals[i].sourceNonX86 = false;
      if (showAlertNonX86) {
        totals[i].sourceNonX86 = true;
        totals[i].sourceWarning += "One or more of the Source environments may not be x86, so above source VCPU totals are not directly comparable to x86 quantities. ";
      }
      if (sourceReplica ) {
        totals[i].sourceWarning += "Some of the source databases are Data Guard replicas. ";
      }
      if (sourceOverlaps) {
        totals[i].sourceWarning += "Duplicate databases and PDBs overlapping with their CDBs are not included the source environment totals. ";
      }
      // if (rdsReplica && !sourceReplica) {
      //   totals[i].sourceWarning += "RDS is sized for Replicas but replicas are not part of source totals. ";
      // }

    }
  }
  
  // Calculate ODB totals if infrastructure is provided
  if (infrastructure) {
    for (let infrai = 0; infrai < infrastructure.length; infrai++) {
      const infra = infrastructure[infrai];
      const totalScenarioIndex = totalScenarioNames.indexOf(infra.scenarioName);
      totals[totalScenarioIndex].totalODBcost += infra.monthly_estimate * 1;
      totals[totalScenarioIndex].totalODBUsage += infra.UsageUnits_billed * 1;
      totals[totalScenarioIndex].totalODBUsageUnits = infra.Usage_units;
      totals[totalScenarioIndex].totalODBdbservercost += infra.dbserver_cost * 1;
      totals[totalScenarioIndex].totalODBstorageservercost += infra.storage_cost * 1;
      totals[totalScenarioIndex].totalODBUsagecost += infra.Usage_cost * 1;
    }
  }
  
  // Calculate RDS Consolidation totals if consolidatedServers is provided
  if (consolidatedServers) {
    for (let infrai = 0; infrai < consolidatedServers.length; infrai++) {
      const server = consolidatedServers[infrai];
      const totalScenarioIndex = totalScenarioNames.indexOf(server.scenarioName);
      totals[totalScenarioIndex].totalRDSConsolidatedcost += server.targetconfig_rds.monthly_estimate * 1;
      totals[totalScenarioIndex].totalRDSConsolidatedComputecost += server.targetconfig_rds.shape_cost * 1;
      totals[totalScenarioIndex].totalRDSConsolidatedStoragecost += server.targetconfig_rds.ebs_data_cost * 1;
      totals[totalScenarioIndex].totalRDSConsolidatedCount += 1;
      totals[totalScenarioIndex].totalRDSConsolidatedVCPU += server.targetconfig_rds.shape_max_vcpu * 1;
      if (server.UseMAZ === "Yes") {
        totals[totalScenarioIndex].totalRDSConsolidatedVCPU += server.targetconfig_rds.shape_max_vcpu * 1;
      }
    }
  }

  // Round numeric totals to 2 decimal places
  for (let i = 0; i < totals.length; i++) {
    totals[i].totalRDScost = Math.round(totals[i].totalRDScost * 100) / 100;
    totals[i].totalRDSComputecost = Math.round(totals[i].totalRDSComputecost * 100) / 100;
    totals[i].totalRDSStoragecost = Math.round(totals[i].totalRDSStoragecost * 100) / 100;
    totals[i].totalAuroracost = Math.round(totals[i].totalAuroracost * 100) / 100;
    totals[i].totalAuroraComputecost = Math.round(totals[i].totalAuroraComputecost * 100) / 100;
    totals[i].totalAuroraStoragecost = Math.round(totals[i].totalAuroraStoragecost * 100) / 100;
    totals[i].totalEC2FSxNcost = Math.round(totals[i].totalEC2FSxNcost * 100) / 100;
    totals[i].totalEC2FSxNComputecost = Math.round(totals[i].totalEC2FSxNComputecost * 100) / 100;
    totals[i].totalEC2FSxNStoragecost = Math.round(totals[i].totalEC2FSxNStoragecost * 100) / 100;
    totals[i].totalEC2FSxNVCPU = Math.round(totals[i].totalEC2FSxNVCPU * 100) / 100;
    totals[i].totalADBSCost = Math.round(totals[i].totalADBSCost * 100) / 100;
    totals[i].totalADBSEcpuCost = Math.round(totals[i].totalADBSEcpuCost * 100) / 100;
    totals[i].totalADBSStorageCost = Math.round(totals[i].totalADBSStorageCost * 100) / 100;
    totals[i].totalODBUsage = Math.round(totals[i].totalODBUsage * 100) / 100;
    totals[i].totalODBcost = Math.round(totals[i].totalODBcost * 100) / 100;
    totals[i].totalODBdbservercost = Math.round(totals[i].totalODBdbservercost * 100) / 100;
    totals[i].totalODBstorageservercost = Math.round(totals[i].totalODBstorageservercost * 100) / 100;
    totals[i].totalODBUsagecost = Math.round(totals[i].totalODBUsagecost * 100) / 100;
    totals[i].totalRDSConsolidatedcost = Math.round(totals[i].totalRDSConsolidatedcost * 100) / 100;
    totals[i].totalRDSConsolidatedComputecost = Math.round(totals[i].totalRDSConsolidatedComputecost * 100) / 100;
    totals[i].totalRDSConsolidatedStoragecost = Math.round(totals[i].totalRDSConsolidatedStoragecost * 100) / 100;
  }

  // console.log("totals", totals);
  
  
  return {
    totals,
    totalScenarioNames
  };
}

export { calculateTotals };
